<template>
  <t-divider style="margin: 5px 0px" />
  <p>{{ $t("节点配置") }}</p>
  <t-form-item name="mode" :required-mark="false" style="margin-bottom: 10px">
    <t-space>
      <t-radio-group v-model="formData.mode" @change="onModeChange">
        <t-radio :value="ReplyMode.KNOWLEDGE">{{
          $t("检索知识库回复")
        }}</t-radio>
        <t-radio :value="ReplyMode.FIXED">{{ $t("固定回复") }}</t-radio>
      </t-radio-group>
    </t-space>
  </t-form-item>

  <!-- 固定回复模式才显示内容编辑器 -->
  <t-form-item
    v-if="formData.mode === ReplyMode.FIXED"
    name="content"
    :required-mark="false"
    :rules="requireRule"
  >
    <div class="reply-content-container">
      <t-space>
        <t-radio-group :value="formData.type" @change="onTypeChange">
          <t-radio value="1">{{ $t("纯文本") }}</t-radio>
          <t-radio value="2">{{ $t("富文本") }}</t-radio>
        </t-radio-group>
      </t-space>
      <t-textarea
        v-if="formData.type === ReplyType.TEXT"
        v-model="formData.content"
        :autosize="{ minRows: 5 }"
        :maxlength="maxLimits?.maxAnswerLength ?? 2000"
        :placeholder="$t('请输入')"
      />
      <rich-text-editor
        v-if="formData.type === ReplyType.RICH_TEXT"
        :value="formData.content"
        @updateContent="updateContent"
      />
    </div>
  </t-form-item>
  <span v-else style="color: #6d778c">{{ $t("包含问答库和文档问答") }}</span>
</template>

<script lang="ts" setup>
import { inject, reactive, Ref, watch } from "vue";
import { trim, get } from "lodash-es";
import turndownService from "@/utils/turndownService";
import { t } from "@/utils/i18n";
import {
  ReplyMode,
  EndReplyNodeData,
  ReplyType,
} from "@/types/endReplyNodeType";
import { getHtml } from "@/utils/markedUtil";

const emit = defineEmits(["updateData"]);

const maxLimits = inject<Ref<{ maxAnswerLength: number }>>("maxLimits");

const props = defineProps<{
  data: EndReplyNodeData;
}>();

const formData = reactive<EndReplyNodeData>({
  mode: props.data.mode ?? ReplyMode.KNOWLEDGE,
  content:
    props.data.type === ReplyType.RICH_TEXT
      ? (getHtml(props.data.content ?? "") as string)
      : (props.data.content ?? ""),
  type: props.data.type ?? ReplyType.TEXT,
});

const onModeChange = (mode: ReplyMode) => {
  formData.mode = mode;
  if (mode === ReplyMode.KNOWLEDGE) {
    formData.content = "";
  }
};

const onTypeChange = (v: ReplyType) => {
  formData.type = v;
  formData.content = "";
};

const requireRule = [
  {
    validator: (_str) => {
      const str =
        formData.type === ReplyType.RICH_TEXT
          ? turndownService.turndown(trim(_str))
          : trim(_str);
      return !!str;
    },
    message: t("不能为空"),
    trigger: "change",
  },
  {
    validator: (_str) => {
      const str =
        formData.type === ReplyType.RICH_TEXT
          ? turndownService.turndown(trim(_str))
          : trim(_str);
      return (
        get(str, "length", 0) <= (maxLimits?.value?.maxAnswerLength ?? 2000)
      );
    },
    message: t("不超过{maxLength}字符", {
      maxLength: maxLimits?.value?.maxAnswerLength ?? 2000,
    }),
    trigger: "change",
  },
];

// 处理编辑器内容变化
const updateContent = (content: string) => {
  formData.content = content;
};

watch(formData, (newVal) => {
  emit("updateData", newVal);
});
</script>
<style lang="less" scoped>
.reply-content-container {
  display: flex;
  flex-direction: column;
  gap: 20px;
  width: 100%;
  border-radius: 8px;
  padding: 16px;
  background-color: #f9fafc;
  border: 1px solid #e4e8ee;
  :deep(.quillContainer) {
    background-color: #fff;
  }
}
</style>
